import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Sparkles, Upload, CheckCircle, ArrowRight, X, FileImage, AlertCircle } from 'lucide-react';
import CorporateHeader from './CorporateHeader';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const USAGE_OPTIONS = [
  'Social Media', 'Business Branding', 'Website', 'Speaking / Press',
  'Dating Profile', 'Personal Use', 'Event / Celebration', 'Other',
];

const AIFlyersIntakePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id') || '';
  const packageId = searchParams.get('package') || 'ai_flyer';

  const [form, setForm] = useState({
    fullName: '', email: '', phone: '',
    occasion: '', flyerWording: '', colorScheme: '', datesAndTimes: '',
    photoUsage: '', photoUsageOther: '', additionalNotes: '',
    qrCodeOption: 'no', qrCashApp: '', qrVenmo: '', qrZelle: '',
  });
  const [photos, setPhotos] = useState([]);
  const [qrImage, setQrImage] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleInput = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    const valid = files.filter(f => ['image/jpeg', 'image/png', 'image/jpg'].includes(f.type) && f.size <= 10 * 1024 * 1024);
    if (valid.length !== files.length) setError('Only JPG/PNG under 10MB accepted.');
    setPhotos(prev => [...prev, ...valid].slice(0, 3));
  };

  const removePhoto = (idx) => setPhotos(prev => prev.filter((_, i) => i !== idx));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.fullName || !form.email) { setError('Name and email are required.'); return; }
    if (photos.length === 0) { setError('Please upload at least one photo.'); return; }
    if (!form.photoUsage) { setError('Please select what this is for.'); return; }
    if (form.photoUsage === 'Other' && !form.photoUsageOther.trim()) { setError('Please specify usage.'); return; }

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('fullName', form.fullName);
      formData.append('email', form.email);
      formData.append('phone', form.phone);
      formData.append('packageId', packageId);
      formData.append('sessionId', sessionId);
      formData.append('occasion', form.occasion);
      formData.append('flyerWording', form.flyerWording);
      formData.append('colorScheme', form.colorScheme);
      formData.append('datesAndTimes', form.datesAndTimes);
      const usageVal = form.photoUsage === 'Other' ? `Other: ${form.photoUsageOther}` : form.photoUsage;
      formData.append('photoUsage', usageVal);
      formData.append('additionalNotes', form.additionalNotes);
      formData.append('qrCodeOption', form.qrCodeOption);
      if (form.qrCodeOption === 'username') {
        formData.append('qrCashApp', form.qrCashApp);
        formData.append('qrVenmo', form.qrVenmo);
        formData.append('qrZelle', form.qrZelle);
      }
      photos.forEach(p => formData.append('photos', p));
      if (form.qrCodeOption === 'upload' && qrImage) {
        formData.append('qrCodeImage', qrImage);
      }

      const res = await fetch(`${API_URL}/api/ai-flyer/intake`, { method: 'POST', body: formData });
      if (!res.ok) { const d = await res.json(); throw new Error(d.detail || 'Submission failed'); }
      setSubmitted(true);
    } catch (err) {
      setError(err.message || 'Failed to submit.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-black">
        <CorporateHeader />
        <div className="pt-28 pb-20">
          <div className="container mx-auto px-4 max-w-2xl text-center">
            <div className="bg-gray-900/50 border border-amber-800/30 rounded-2xl p-8 sm:p-12">
              <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-6" />
              <h1 className="text-3xl font-bold text-white mb-4" data-testid="flyer-submission-confirmed">Your details have been received.</h1>
              <p className="text-amber-400 font-semibold text-lg mb-6">Your project is now in production. You will receive your preview within 24-48 hours.</p>
              <p className="text-gray-500 italic mb-8">"We don't take pictures... we build presence."</p>
              <Button onClick={() => navigate('/')} className="bg-gradient-to-r from-amber-600 to-yellow-600 text-black font-bold px-8 py-3 rounded-full">Return Home</Button>
            </div>
            <div className="mt-8"><p className="text-amber-400/80 text-sm font-semibold tracking-wider">eBlackBook AI Luxury Flyer Design</p><p className="text-gray-600 text-xs mt-1">Powered by eBlackBookListings</p></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      <CorporateHeader />
      <div className="pt-28 pb-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-3xl">
          <div className="text-center mb-10">
            <Sparkles className="w-10 h-10 text-amber-400 mx-auto mb-4" />
            <h1 className="text-3xl sm:text-4xl font-bold text-white mb-2" data-testid="flyer-intake-title">Flyer Design Intake</h1>
            <p className="text-gray-400">Upload your photo and tell us about your flyer.</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Client Details */}
            <div className="bg-gray-900/50 border border-gray-800 rounded-2xl p-6 space-y-4">
              <h2 className="text-lg font-bold text-white mb-2">Client Details</h2>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1.5">Full Name <span className="text-amber-400">*</span></label>
                <Input value={form.fullName} onChange={(e) => handleInput('fullName', e.target.value)} placeholder="Your full name" required className="bg-black border-gray-700 text-white" data-testid="flyer-intake-fullName" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1.5">Email Address <span className="text-amber-400">*</span></label>
                <Input type="email" value={form.email} onChange={(e) => handleInput('email', e.target.value)} placeholder="your@email.com" required className="bg-black border-gray-700 text-white" data-testid="flyer-intake-email" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1.5">Phone Number <span className="text-gray-600">(optional)</span></label>
                <Input value={form.phone} onChange={(e) => handleInput('phone', e.target.value)} placeholder="(555) 000-0000" className="bg-black border-gray-700 text-white" data-testid="flyer-intake-phone" />
              </div>
            </div>

            {/* Photo Upload */}
            <div className="bg-gray-900/50 border border-gray-800 rounded-2xl p-6">
              <h2 className="text-lg font-bold text-white mb-1">Upload Your Photo(s) <span className="text-amber-400">*</span></h2>
              <p className="text-gray-500 text-sm mb-4">Upload 1-3 clear photos (JPG, PNG — Max 10MB each)</p>
              {photos.length < 3 && (
                <label className="flex flex-col items-center justify-center border-2 border-dashed border-gray-700 hover:border-amber-600/50 rounded-xl p-8 cursor-pointer transition-colors mb-4" data-testid="flyer-photo-upload">
                  <Upload className="w-10 h-10 text-gray-500 mb-3" />
                  <span className="text-gray-400 text-sm font-semibold">Click to upload</span>
                  <span className="text-gray-600 text-xs mt-1">JPG, PNG — Max 10MB</span>
                  <input type="file" accept="image/jpeg,image/png,image/jpg" onChange={handleFileChange} className="hidden" multiple />
                </label>
              )}
              {photos.length > 0 && (
                <div className="flex gap-4 flex-wrap">
                  {photos.map((p, i) => (
                    <div key={i} className="relative bg-gray-800 rounded-xl p-3 flex items-center gap-3">
                      <FileImage className="w-8 h-8 text-amber-400" />
                      <div><p className="text-white text-sm truncate max-w-[180px]">{p.name}</p><p className="text-gray-500 text-xs">{(p.size / 1024 / 1024).toFixed(1)} MB</p></div>
                      <button type="button" onClick={() => removePhoto(i)} className="absolute -top-2 -right-2 bg-red-600 rounded-full p-1"><X className="w-3 h-3 text-white" /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Flyer Details */}
            <div className="bg-gray-900/50 border border-gray-800 rounded-2xl p-6 space-y-4">
              <h2 className="text-lg font-bold text-white mb-2">Flyer Details</h2>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1.5">Occasion</label>
                <Input value={form.occasion} onChange={(e) => handleInput('occasion', e.target.value)} placeholder="e.g., Birthday, Business Launch, Anniversary, Promotion..." className="bg-black border-gray-700 text-white" data-testid="flyer-intake-occasion" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1.5">All Wording for the Flyer</label>
                <textarea value={form.flyerWording} onChange={(e) => handleInput('flyerWording', e.target.value)} placeholder="Enter all text you want on the flyer (names, taglines, event details...)" rows={3} className="w-full bg-black border border-gray-700 text-white rounded-lg px-4 py-3 text-sm focus:ring-amber-500 focus:border-amber-500" data-testid="flyer-intake-wording" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1.5">Color Scheme / Theme / Vibe</label>
                <Input value={form.colorScheme} onChange={(e) => handleInput('colorScheme', e.target.value)} placeholder="e.g., Black & gold, all pink, elegant, bold..." className="bg-black border-gray-700 text-white" data-testid="flyer-intake-colorScheme" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1.5">Dates, Times & Addresses <span className="text-gray-600">(if applicable)</span></label>
                <textarea value={form.datesAndTimes} onChange={(e) => handleInput('datesAndTimes', e.target.value)} placeholder="Include any dates, times, and venue/address details..." rows={2} className="w-full bg-black border border-gray-700 text-white rounded-lg px-4 py-3 text-sm focus:ring-amber-500 focus:border-amber-500" data-testid="flyer-intake-dates" />
              </div>
            </div>

            {/* What is this for? */}
            <div className="bg-gray-900/50 border border-gray-800 rounded-2xl p-6 space-y-4">
              <h2 className="text-lg font-bold text-white mb-2">What is this for? <span className="text-amber-400">*</span></h2>
              <select value={form.photoUsage} onChange={(e) => handleInput('photoUsage', e.target.value)} required className="w-full bg-black border border-gray-700 text-white rounded-lg px-4 py-3 text-sm focus:ring-amber-500 focus:border-amber-500 appearance-none" data-testid="flyer-intake-usage">
                <option value="" disabled>Select an option...</option>
                {USAGE_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
              {form.photoUsage === 'Other' && (
                <Input value={form.photoUsageOther} onChange={(e) => handleInput('photoUsageOther', e.target.value)} placeholder="Please specify..." required className="bg-black border-gray-700 text-white" data-testid="flyer-intake-usageOther" />
              )}
            </div>

            {/* QR Code Section */}
            <div className="bg-gray-900/50 border border-gray-800 rounded-2xl p-6 space-y-4">
              <h2 className="text-lg font-bold text-white mb-2">Would you like to include a payment QR code on your flyer?</h2>
              <div className="space-y-3">
                <label className="flex items-center gap-3 cursor-pointer p-3 rounded-lg border border-gray-800 hover:border-amber-600/30 transition-colors">
                  <input type="radio" name="qr" value="upload" checked={form.qrCodeOption === 'upload'} onChange={() => handleInput('qrCodeOption', 'upload')} className="accent-amber-500" />
                  <span className="text-gray-300 text-sm">Yes — Upload QR code image</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer p-3 rounded-lg border border-gray-800 hover:border-amber-600/30 transition-colors">
                  <input type="radio" name="qr" value="username" checked={form.qrCodeOption === 'username'} onChange={() => handleInput('qrCodeOption', 'username')} className="accent-amber-500" />
                  <span className="text-gray-300 text-sm">Yes — Enter username</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer p-3 rounded-lg border border-gray-800 hover:border-amber-600/30 transition-colors">
                  <input type="radio" name="qr" value="no" checked={form.qrCodeOption === 'no'} onChange={() => handleInput('qrCodeOption', 'no')} className="accent-amber-500" />
                  <span className="text-gray-300 text-sm">No</span>
                </label>
              </div>

              {form.qrCodeOption === 'upload' && (
                <div className="mt-4">
                  {!qrImage ? (
                    <label className="flex flex-col items-center justify-center border-2 border-dashed border-gray-700 hover:border-amber-600/50 rounded-xl p-6 cursor-pointer transition-colors" data-testid="flyer-qr-upload">
                      <Upload className="w-8 h-8 text-gray-500 mb-2" />
                      <span className="text-gray-400 text-sm">Upload your QR code image</span>
                      <input type="file" accept="image/*" onChange={(e) => setQrImage(e.target.files[0] || null)} className="hidden" />
                    </label>
                  ) : (
                    <div className="flex items-center gap-3 bg-gray-800 rounded-xl p-3">
                      <FileImage className="w-6 h-6 text-amber-400" />
                      <span className="text-white text-sm truncate">{qrImage.name}</span>
                      <button type="button" onClick={() => setQrImage(null)} className="ml-auto text-red-400 hover:text-red-300 text-sm">Remove</button>
                    </div>
                  )}
                </div>
              )}

              {form.qrCodeOption === 'username' && (
                <div className="mt-4 space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">Cash App</label>
                    <Input value={form.qrCashApp} onChange={(e) => handleInput('qrCashApp', e.target.value)} placeholder="$yourname" className="bg-black border-gray-700 text-white" data-testid="flyer-qr-cashapp" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">Venmo</label>
                    <Input value={form.qrVenmo} onChange={(e) => handleInput('qrVenmo', e.target.value)} placeholder="@yourname" className="bg-black border-gray-700 text-white" data-testid="flyer-qr-venmo" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">Zelle</label>
                    <Input value={form.qrZelle} onChange={(e) => handleInput('qrZelle', e.target.value)} placeholder="Phone or email" className="bg-black border-gray-700 text-white" data-testid="flyer-qr-zelle" />
                  </div>
                </div>
              )}
            </div>

            {/* Additional Notes */}
            <div className="bg-gray-900/50 border border-gray-800 rounded-2xl p-6">
              <h2 className="text-lg font-bold text-white mb-2">Additional Notes</h2>
              <textarea value={form.additionalNotes} onChange={(e) => handleInput('additionalNotes', e.target.value)} placeholder="Any special requests, inspiration, or details..." rows={4} className="w-full bg-black border border-gray-700 text-white rounded-lg px-4 py-3 text-sm focus:ring-amber-500 focus:border-amber-500" data-testid="flyer-intake-notes" />
            </div>

            {error && (
              <div className="flex items-center gap-2 bg-red-900/20 border border-red-800 text-red-400 rounded-xl p-4 text-sm">
                <AlertCircle className="w-5 h-5 flex-shrink-0" />{error}
              </div>
            )}

            <Button type="submit" disabled={submitting} className="w-full bg-gradient-to-r from-amber-600 to-yellow-600 hover:from-amber-700 hover:to-yellow-700 text-black font-bold py-4 text-lg rounded-full" data-testid="flyer-intake-submit">
              {submitting ? 'Submitting...' : 'Submit My Flyer Order'}
              {!submitting && <ArrowRight className="w-5 h-5 ml-2" />}
            </Button>
          </form>

          <div className="mt-12 text-center">
            <p className="text-amber-400/80 text-sm font-semibold tracking-wider">eBlackBook AI Luxury Flyer Design</p>
            <p className="text-gray-600 text-xs mt-1">Powered by eBlackBookListings</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIFlyersIntakePage;
